// The tab store for the supplement interaction checker page (STACK-587).
//
// Design: docs/supplement-interaction-checker.md §3 steps 2, 3 and 8, and §3.1
// "Where picks live". The rules this module keeps:
//
//   - The picks live in `sessionStorage`, under this page's own key, apart
//     from the composer's `guestMedications` (whose cap and checked-stack
//     memory are the composer's). Never `localStorage`, a cookie or a URL.
//   - A medication is `{ in_rxcui, ingredient_name, display_name }` and
//     nothing more; a supplement is `{ key, name }`, a key from our list.
//   - At most CHECKER_MAX_MEDICATIONS and CHECKER_MAX_SUPPLEMENTS (Tanu,
//     2026-10-16). The route accepts at most as many
//     (`backend/endpoints/interaction_checker.py`); a backend test reads this
//     file and keeps the page's caps within the route's.
//   - Signing in or up empties it (`AuthContext` → syncCheckerPicksWithUser).
//     Restoring a signed-in session on reload does not: the store remembers
//     whose picks they are, so only a different account (or a visitor
//     becoming one) clears them.
//   - Storage that is blocked or full falls back to memory for the visit, so
//     the page still works; it just forgets on reload.

export const CHECKER_MAX_MEDICATIONS = 3;
export const CHECKER_MAX_SUPPLEMENTS = 3;

const STORAGE_KEY = 'interactionCheckerPicks';
// Whose picks these are: a uid, or '' for a signed-out visitor.
const OWNER_KEY = 'interactionCheckerPicksOwner';

const EMPTY = { medications: [], supplements: [] };

const listeners = new Set();

// Once storage fails, the visit's picks live here instead.
let memory = null;
let memoryOwner = null;

const storage = () => {
  try {
    return window.sessionStorage || null;
  } catch (_) {
    // Storage blocked (privacy mode, sandboxed WebView).
    return null;
  }
};

const isPositiveInt = (v) => Number.isInteger(v) && v > 0;

// Only the named fields, and only well-formed ones: whatever else a caller
// passes (a search result, a details response) never reaches storage.
const cleanMedication = (item) => {
  if (!item || typeof item !== 'object') return null;
  const in_rxcui = typeof item.in_rxcui === 'string' ? parseInt(item.in_rxcui, 10) : item.in_rxcui;
  if (!isPositiveInt(in_rxcui)) return null;
  const ingredient_name = typeof item.ingredient_name === 'string' ? item.ingredient_name : '';
  const display_name = typeof item.display_name === 'string' && item.display_name
    ? item.display_name
    : ingredient_name;
  if (!display_name) return null;
  return { in_rxcui, ingredient_name, display_name };
};

const cleanSupplement = (item) => {
  if (!item || typeof item !== 'object') return null;
  if (typeof item.key !== 'string' || !item.key) return null;
  const name = typeof item.name === 'string' && item.name ? item.name : item.key;
  return { key: item.key, name };
};

const dedupe = (list, idOf) => {
  const seen = new Set();
  return list.filter((item) => {
    const id = idOf(item);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const clean = (state) => ({
  medications: dedupe(
    (Array.isArray(state?.medications) ? state.medications : []).map(cleanMedication).filter(Boolean),
    (m) => m.in_rxcui,
  ).slice(0, CHECKER_MAX_MEDICATIONS),
  supplements: dedupe(
    (Array.isArray(state?.supplements) ? state.supplements : []).map(cleanSupplement).filter(Boolean),
    (s) => s.key,
  ).slice(0, CHECKER_MAX_SUPPLEMENTS),
});

export const getCheckerPicks = () => {
  if (memory) return memory;
  const s = storage();
  if (!s) {
    memory = EMPTY;
    return memory;
  }
  try {
    return clean(JSON.parse(s.getItem(STORAGE_KEY) || 'null'));
  } catch (_) {
    // Unreadable: start the visit afresh in memory.
    memory = EMPTY;
    return memory;
  }
};

const write = (state) => {
  const next = clean(state);
  const s = memory ? null : storage();
  if (s) {
    try {
      if (next.medications.length || next.supplements.length) s.setItem(STORAGE_KEY, JSON.stringify(next));
      else s.removeItem(STORAGE_KEY);
    } catch (_) {
      // Quota or blocked storage: keep the visit's picks in memory instead.
      memory = next;
    }
  } else {
    memory = next;
  }
  listeners.forEach((fn) => fn(next));
  return next;
};

/**
 * Adds a medication. Returns 'added', 'duplicate', 'full' or 'invalid', as
 * the composer's store does, so a caller never shows a pick that did not stick.
 */
export const addCheckerMedication = (item) => {
  const med = cleanMedication(item);
  if (!med) return 'invalid';
  const current = getCheckerPicks();
  if (current.medications.some((m) => m.in_rxcui === med.in_rxcui)) return 'duplicate';
  if (current.medications.length >= CHECKER_MAX_MEDICATIONS) return 'full';
  write({ ...current, medications: [...current.medications, med] });
  return 'added';
};

export const removeCheckerMedication = (inRxcui) => {
  const current = getCheckerPicks();
  write({ ...current, medications: current.medications.filter((m) => m.in_rxcui !== inRxcui) });
};

/** Adds a supplement from our list. Same outcomes as addCheckerMedication. */
export const addCheckerSupplement = (item) => {
  const sup = cleanSupplement(item);
  if (!sup) return 'invalid';
  const current = getCheckerPicks();
  if (current.supplements.some((s) => s.key === sup.key)) return 'duplicate';
  if (current.supplements.length >= CHECKER_MAX_SUPPLEMENTS) return 'full';
  write({ ...current, supplements: [...current.supplements, sup] });
  return 'added';
};

export const removeCheckerSupplement = (key) => {
  const current = getCheckerPicks();
  write({ ...current, supplements: current.supplements.filter((s) => s.key !== key) });
};

export const clearCheckerPicks = () => {
  write(EMPTY);
};

/**
 * Called by `AuthContext` on every auth state: `uid` for a signed-in user,
 * null when signed out. Empties the picks when they are not this user's — a
 * visitor signing in or up, another account, or a member signing out — and
 * keeps them when the same user's session is restored on reload.
 */
export const syncCheckerPicksWithUser = (uid) => {
  const owner = uid || '';
  const s = memory ? null : storage();
  let previous = memoryOwner;
  if (s) {
    try {
      previous = s.getItem(OWNER_KEY);
    } catch (_) {
      previous = memoryOwner;
    }
  }
  // Signing in (or switching account) empties picks that are not this user's.
  // Signing out empties a member's picks, so the next person on the tab never
  // sees them; a visitor's own first load keeps theirs.
  const signedIn = Boolean(uid) && previous !== owner;
  const signedOut = !uid && Boolean(previous);
  if (signedIn || signedOut) clearCheckerPicks();
  memoryOwner = owner;
  if (s) {
    try {
      s.setItem(OWNER_KEY, owner);
    } catch (_) {
      // Memory holds it.
    }
  }
};

/** Calls `fn(picks)` after every change; returns the unsubscribe. */
export const subscribeCheckerPicks = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
